import {
  applyMods, applyMove, applyRotate, cellIndex, cellsOf, computeGroups, findHint, NO_MODS, occupants, parOf, progress, rowsOf, shuffle, starLimits, starsFor, unitOf,
  type Board, type Cell, type Grid, type Hint, type Mods, type Move, type MoveResult, type Par,
} from '../puzzle';
import type { BoardScene, Mark } from './BoardScene';
import type { Hud } from '../ui/hud';
import { onTap, type Selection } from './tapSelect';
import { cellCenter, pointToCell, screenToWorld, worldToScreen, type Viewport } from './layout';
import { tapFeedback } from '../core/haptics';

const DRAG_THRESHOLD = 8;
const SELECT_COLOR = 0x2f6f9f; // 深蓝：在浅色纸艺图块上也清楚
const HINT_COLOR = 0xf2a93b;
const HINT_MS = 2500;

type Gesture = { id: number; startX: number; startY: number; anchor: number | null; unit: number[]; dragging: boolean };

export class PlaySession {
  board: Board;
  /** 列数（方形棋盘 = 边长） */
  n: number;
  /** 行数（方形 = n；塔罗竖版 4×6 … 7×10） */
  rows: number;
  seed: number;
  moves = 0;
  elapsedMs = 0;
  timerRunning = false;
  completed = false;
  paused = false;
  selection: Selection = null;
  private gesture: Gesture | null = null;
  private hintMarks: Mark[] = [];
  private hintLeft = 0;
  private previewing = false;
  private vp: Viewport = { width: 1, height: 1 };
  /** 通关回调（App 接管：通关演出 → 立体书） */
  onComplete: (() => void) | null = null;
  /** 棋盘状态变了（走了一步 / 重置）：App 写入 inProgress */
  onChange: (() => void) | null = null;
  /** 产生了新的粘连（新手引导：第一次粘连时暂停讲解） */
  onMerge: (() => void) | null = null;
  /** 引导讲解期间锁住输入 */
  locked = false;
  /** 本次进入拼图页以来真正计时的总时长（不因重置 / 恢复清零）：统计"总用时"按它累加 */
  playedMs = 0;
  /** 每日挑战：固定打乱种子，[重置] 回到同一个开局 */
  fixedSeed = false;
  /** 本关玩法（旋转块 / 背面块，见 core/levels） */
  readonly mods: Mods;
  /** 本局开局棋盘的标准步数（星级，见 puzzle/stars） */
  par: Par;
  /** 通关时的星级（1..3）；未通关 = null */
  stars: 1 | 2 | 3 | null = null;

  constructor(private readonly scene: BoardScene, private readonly hud: Hud, grid: Grid | number, seed: number, mods: Mods = NO_MODS) {
    const g = typeof grid === 'number' ? { cols: grid, rows: grid } : grid;
    this.n = g.cols;
    this.rows = g.rows;
    this.seed = seed;
    this.mods = { ...mods };
    this.board = this.deal(this.grid, seed);
    this.par = parOf(this.board);
    hud.bind({ preview: () => this.preview(), hint: () => this.hint(), restart: () => this.restart(this.fixedSeed ? this.seed : this.seed + 1) });
    this.scene.setBoard(this.board, true);
    this.refreshHud();
  }

  get grid(): Grid {
    return { cols: this.n, rows: this.rows };
  }

  get viewport(): Viewport {
    return this.vp;
  }

  setViewport(vp: Viewport): void {
    this.vp = vp;
    this.scene.setViewport(vp);
    this.hud.placeFrame(vp, this.scene.boardRect);
  }

  /** 同一 seed 永远得到同一开局：洗牌 + 本关玩法（旋转 / 背面） */
  private deal(g: Grid, seed: number): Board {
    return applyMods(shuffle(g.cols, seed, undefined, g.rows), this.mods, seed);
  }

  restart(seed: number, grid: Grid = this.grid): void {
    this.n = grid.cols;
    this.rows = grid.rows;
    this.seed = seed;
    this.reset(this.deal(grid, seed));
  }

  setBoardForTest(board: Board): void {
    this.n = board.n;
    this.rows = rowsOf(board);
    this.reset(board);
  }

  /** 从存档恢复（中途退出后继续）：计时从下一次触碰棋盘继续累计；旧存档没有 par 时按 seed 重算开局 */
  restore(board: Board, seed: number, moves: number, elapsedMs: number, par?: Par): void {
    this.seed = seed;
    this.n = board.n;
    this.rows = rowsOf(board);
    this.reset(board, false);
    this.par = par ?? parOf(this.deal(this.grid, seed));
    this.moves = moves;
    this.elapsedMs = elapsedMs;
    this.refreshHud();
  }

  private reset(board: Board, notify = true): void {
    this.board = board;
    this.moves = 0;
    this.elapsedMs = 0;
    this.timerRunning = false;
    this.completed = false;
    this.stars = null;
    this.par = parOf(board);
    this.selection = null;
    this.gesture = null;
    this.hintMarks = [];
    this.hintLeft = 0;
    this.scene.setBoard(board, true);
    this.applyMarks();
    this.refreshHud();
    if (notify) this.onChange?.();
  }

  pointerDown(sx: number, sy: number, id: number): void {
    if (this.completed || this.gesture || this.locked) return;
    const w = screenToWorld(this.vp, sx, sy);
    const cell = pointToCell(this.scene.boardRect, this.grid, w.x, w.y, false);
    if (cell) this.timerRunning = true;
    const anchor = cell ? this.tileAt(cell) : null;
    this.gesture = { id, startX: sx, startY: sy, anchor, unit: anchor === null ? [] : unitOf(this.board, anchor), dragging: false };
  }

  pointerMove(sx: number, sy: number, id: number): void {
    const g = this.gesture;
    if (!g || g.id !== id || g.anchor === null) return;
    const dx = sx - g.startX;
    const dy = sy - g.startY;
    if (!g.dragging && Math.hypot(dx, dy) > DRAG_THRESHOLD) {
      g.dragging = true;
      this.selection = null;
      this.applyMarks();
    }
    if (g.dragging) this.scene.setOffset(g.unit, dx, -dy, 5);
  }

  pointerUp(sx: number, sy: number, id: number): void {
    const g = this.gesture;
    if (!g || g.id !== id) return;
    this.gesture = null;
    if (g.dragging && g.anchor !== null) {
      const c = cellCenter(this.scene.boardRect, this.grid, this.board.pos[g.anchor]);
      const to = pointToCell(this.scene.boardRect, this.grid, c.x + (sx - g.startX), c.y - (sy - g.startY), true);
      if (to) this.commit({ anchor: g.anchor, to }, g.unit);
      else void this.scene.settle(g.unit, 150); // 坐标为 NaN 时只弹回
      return;
    }
    // 从棋盘外起手（无锚点）并拖远的手势不是点按
    if (Math.hypot(sx - g.startX, sy - g.startY) > DRAG_THRESHOLD) {
      this.applyMarks();
      return;
    }
    const w = screenToWorld(this.vp, sx, sy);
    const r = onTap(this.board, this.selection, pointToCell(this.scene.boardRect, this.grid, w.x, w.y, false));
    this.selection = r.selection;
    if (r.move) this.commit(r.move, unitOf(this.board, r.move.anchor));
    else if (r.rotate !== undefined) this.rotate(r.rotate);
    else this.applyMarks();
  }

  pointerCancel(id: number): void {
    const g = this.gesture;
    if (!g || g.id !== id) return;
    this.gesture = null;
    if (g.dragging) void this.scene.settle(g.unit, 150);
  }

  private commit(move: Move, unit: number[]): void {
    const res = applyMove(this.board, move);
    const invalid = res.events.find((e) => e.type === 'invalid');
    if (invalid && invalid.type === 'invalid') {
      void this.scene.settle(unit, 150);
      if (invalid.reason === 'out-of-bounds') tapFeedback();
      this.applyMarks();
      return;
    }
    this.accept(res);
  }

  /** 点按旋转块：顺时针转 90°，算一步；转动动画与背面翻开都由 BoardScene 对比新旧棋盘播放 */
  private rotate(tile: number): void {
    const res = applyRotate(this.board, tile);
    if (res.events.some((e) => e.type === 'invalid')) return this.applyMarks();
    tapFeedback();
    this.accept(res);
  }

  /** 一步有效走子（交换 / 旋转）之后：记步、动画、通关判定、存档 */
  private accept(res: MoveResult): void {
    this.board = res.board;
    this.moves += 1;
    this.hintMarks = [];
    this.hintLeft = 0;
    this.scene.setBoard(this.board);
    for (const e of res.events) {
      if (e.type === 'moved') void this.scene.slideFrom(e.moves);
      if (e.type === 'merged') {
        const touched = new Set(e.newEdges.flat());
        void this.scene.mergePop(e.groups.filter((g) => g.some((t) => touched.has(t))));
      }
    }
    this.applyMarks();
    this.refreshHud();
    if (res.events.some((e) => e.type === 'completed')) return this.finish();
    this.onChange?.();
    if (res.events.some((e) => e.type === 'merged')) this.onMerge?.();
  }

  private finish(): void {
    this.completed = true;
    this.stars = starsFor(this.moves, this.par);
    this.timerRunning = false;
    this.selection = null;
    this.applyMarks();
    this.onComplete?.();
  }

  /** 供测试和截图：当前提示的走法（不改变状态） */
  hintMove(): Move | null {
    const h = findHint(this.board);
    return h && 'move' in h ? h.move : null;
  }

  /** 供测试：完整提示（走法 / 放回原位 / 转正） */
  hintFull(): Hint {
    return findHint(this.board);
  }

  preview(): void {
    if (this.previewing || this.completed) return;
    this.previewing = true;
    void this.scene.showPreview(3000).then(() => (this.previewing = false));
  }

  hint(): void {
    if (this.completed) return;
    const h = findHint(this.board);
    if (!h) return;
    if ('rotate' in h) {
      this.hintMarks = [{ cells: [this.board.pos[h.rotate.tile]], color: HINT_COLOR, pulse: true }];
    } else if ('move' in h) {
      const unit = unitOf(this.board, h.move.anchor);
      const from = this.board.pos[h.move.anchor];
      const dr = h.move.to.row - from.row;
      const dc = h.move.to.col - from.col;
      const now = unit.map((t) => this.board.pos[t]);
      const dest = now.map((c) => ({ row: c.row + dr, col: c.col + dc }));
      this.hintMarks = [
        { cells: now, color: HINT_COLOR, pulse: true },
        { cells: dest, color: HINT_COLOR },
      ];
    } else {
      this.hintMarks = [
        { cells: [this.board.pos[h.place.tile]], color: HINT_COLOR, pulse: true },
        { cells: [h.place.to], color: HINT_COLOR },
      ];
    }
    this.hintLeft = HINT_MS;
    this.applyMarks();
  }

  update(dtMs: number): void {
    if (this.timerRunning && !this.paused && !this.locked) {
      this.elapsedMs += dtMs;
      this.playedMs += dtMs;
    }
    if (this.hintLeft > 0) {
      this.hintLeft -= dtMs;
      if (this.hintLeft <= 0) {
        this.hintMarks = [];
        this.applyMarks();
      }
    }
    this.scene.update(dtMs);
    this.refreshHud();
  }

  toText() {
    const r = this.scene.boardRect;
    const tl = worldToScreen(this.vp, r.cx - r.w / 2, r.cy + r.h / 2);
    return {
      page: 'play' as const,
      n: this.n,
      rows: this.rows,
      seed: this.seed,
      cells: cellsOf(this.board),
      groups: computeGroups(this.board),
      progress: progress(this.board),
      moves: this.moves,
      elapsedMs: Math.round(this.elapsedMs),
      completed: this.completed,
      selection: this.selection?.anchor ?? null,
      mods: this.mods,
      /** 下标 = tileId */
      rot: this.board.rot ?? null,
      down: this.board.down ?? null,
      par: this.par,
      starLimits: starLimits(this.par),
      stars: this.stars,
      hintActive: this.hintLeft > 0,
      previewing: this.previewing,
      boardRect: { x: tl.x, y: tl.y, size: r.w, w: r.w, h: r.h },
    };
  }

  private applyMarks(): void {
    const marks: Mark[] = [];
    if (this.selection) {
      marks.push({ cells: unitOf(this.board, this.selection.anchor).map((t) => this.board.pos[t]), color: SELECT_COLOR });
    }
    marks.push(...this.hintMarks);
    this.scene.setMarks(marks);
  }

  private tileAt(cell: Cell): number {
    return occupants(this.board)[cellIndex(cell, this.n)];
  }

  private refreshHud(): void {
    this.hud.set({ timeMs: this.elapsedMs, progress: progress(this.board) });
  }
}
